import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Loader2, Mail, Lock, ArrowRight, MapPinOff, ShieldAlert } from 'lucide-react';
import { toast } from 'sonner';

// Try to get GPS silently — resolves to null after 5s or on any error/denial.
const requestGpsSilent = () => new Promise((resolve) => {
  if (!navigator.geolocation) return resolve(null);
  let done = false;
  const finish = (val) => { if (!done) { done = true; resolve(val); } };
  setTimeout(() => finish(null), 5000);
  navigator.geolocation.getCurrentPosition(
    (pos) => finish({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
    () => finish(null),
    { enableHighAccuracy: false, timeout: 4500, maximumAge: 60000 }
  );
});

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [geoBlocked, setGeoBlocked] = useState(null); // { country, is_vpn, message }
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error('Veuillez remplir tous les champs');
      return;
    }

    setLoading(true);
    setGeoBlocked(null);
    try {
      // Request GPS silently in parallel (may be null if refused or timeout)
      const gps = await requestGpsSilent();
      // Normalize on client side too: trim whitespace, lowercase email
      const user = await login(email.trim().toLowerCase(), password, gps);
      toast.success(`Bienvenue ${user.name} !`);
      
      if (user.role === 'admin') {
        navigate('/admin');
      } else if (user.role === 'gouvernant') {
        navigate('/gouvernant');
      } else if (user.role === 'technicien') {
        navigate('/tech');
      } else {
        navigate('/app');
      }
    } catch (error) {
      console.error('Login error:', error);
      const detail = error.response?.data?.detail;
      // 403 GEO_BLOCKED — dedicated full-screen message
      if (error.response?.status === 403 && detail && typeof detail === 'object' && detail.code === 'GEO_BLOCKED') {
        setGeoBlocked({
          country: detail.country || 'Inconnu',
          is_vpn: !!detail.is_vpn,
          message: detail.message,
        });
      } else {
        const msg = typeof detail === 'string' ? detail : (detail?.message || 'Identifiants incorrects');
        toast.error(msg);
      }
    } finally {
      setLoading(false);
    }
  };

  // Geo-blocked full-screen message
  if (geoBlocked) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-600 to-red-800 flex items-center justify-center p-6" data-testid="geo-blocked-screen">
        <div className="max-w-lg w-full bg-white rounded-2xl shadow-2xl overflow-hidden">
          <div className="bg-gradient-to-r from-red-600 to-red-700 p-6 text-white flex items-center gap-3">
            <ShieldAlert className="h-10 w-10 flex-shrink-0" />
            <div>
              <h2 className="text-xl font-bold">Accès refusé</h2>
              <p className="text-red-100 text-sm">Connexion depuis l'étranger bloquée</p>
            </div>
          </div>
          <div className="p-6 space-y-4">
            <div className="flex items-center gap-2 text-slate-700">
              <MapPinOff className="h-5 w-5 text-red-500" />
              <span className="text-sm">
                Pays détecté : <strong>{geoBlocked.country}</strong>
              </span>
            </div>
            {geoBlocked.is_vpn && (
              <div className="bg-amber-50 border border-amber-200 text-amber-800 rounded-lg p-3 text-sm">
                ⚠️ Un VPN ou proxy a été détecté sur votre connexion. Merci de le désactiver et de réessayer.
              </div>
            )}
            <p className="text-slate-600 text-sm leading-relaxed">
              {geoBlocked.message}
            </p>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-900">
              <strong>Que faire ?</strong>
              <ul className="mt-1 ml-4 list-disc space-y-1">
                <li>Vérifiez que vous êtes bien en France</li>
                <li>Désactivez tout VPN ou proxy</li>
                <li>Si vous devez travailler depuis l'étranger, contactez l'administrateur pour qu'il autorise votre compte</li>
              </ul>
            </div>
            <Button
              variant="outline"
              className="w-full mt-4"
              onClick={() => setGeoBlocked(null)}
              data-testid="geo-blocked-retry-btn"
            >
              Réessayer
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#1F3A8A] flex">
      {/* Left Side - Branding (Desktop only) */}
      <div className="hidden lg:flex lg:w-1/2 flex-col justify-between p-12 relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-20 w-64 h-64 rounded-full bg-white"></div>
          <div className="absolute bottom-32 right-10 w-96 h-96 rounded-full bg-white"></div>
          <div className="absolute top-1/2 left-1/3 w-48 h-48 rounded-full bg-white"></div>
        </div>
        
        <div className="relative z-10">
          <img 
            src="/logo-conciergerie.png" 
            alt="PlayMCB" 
            className="h-20 w-auto object-contain"
          />
        </div>
        
        <div className="relative z-10">
          <h2 className="text-4xl lg:text-5xl font-bold text-white leading-tight mb-6">
            Gérez vos<br />
            plannings en<br />
            toute simplicité
          </h2>
          <p className="text-sky-200 text-lg max-w-md">
            Une application pensée pour les équipes de ménage. 
            Simple, efficace, mobile.
          </p>
        </div>
        
        <div className="relative z-10 text-sky-300 text-sm">
          © 2024 GROUPE MCB - Tous droits réservés
        </div>
      </div>

      {/* Right Side - Login Form */}
      <div className="w-full lg:w-1/2 flex flex-col min-h-screen lg:min-h-0 lg:justify-center bg-slate-50 lg:rounded-l-[3rem]">
        
        {/* Mobile Header with gradient */}
        <div className="lg:hidden bg-gradient-to-b from-[#1F3A8A] to-[#2A4A9A] pt-10 pb-14 px-6 rounded-b-[2rem] relative overflow-hidden">
          {/* Decorative circles */}
          <div className="absolute top-0 right-0 w-32 h-32 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2"></div>
          <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2"></div>
          
          {/* Logo - directly without background box */}
          <div className="relative z-10 flex justify-center mb-5">
            <img 
              src="/logo-conciergerie.png" 
              alt="PlayMCB" 
              className="h-16 w-auto object-contain"
            />
          </div>
          
          {/* Welcome text */}
          <div className="relative z-10 text-center">
            <h1 className="text-2xl font-bold text-white mb-2">Bienvenue !</h1>
            <p className="text-sky-200 text-sm">
              Connectez-vous pour accéder à vos plannings
            </p>
          </div>
        </div>

        {/* Form Container */}
        <div className="flex-1 flex items-start lg:items-center justify-center px-6 py-8 lg:p-12 -mt-6 lg:mt-0">
          <div className="w-full max-w-md">
            
            {/* Mobile: Form card with shadow */}
            <div className="lg:hidden bg-white rounded-2xl shadow-xl p-6 mb-6">
              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-slate-700 font-medium text-sm">
                    Adresse email
                  </Label>
                  <div className="relative">
                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-[#1F3A8A]/40" />
                    <Input
                      id="email"
                      type="email"
                      placeholder="votre@email.fr"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      autoCapitalize="none"
                      autoCorrect="off"
                      spellCheck={false}
                      className="h-14 pl-12 bg-slate-50 border-slate-200 rounded-xl text-base focus:border-[#1F3A8A] focus:ring-[#1F3A8A]/20 focus:bg-white transition-colors"
                      data-testid="login-email-input"
                      disabled={loading}
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="password" className="text-slate-700 font-medium text-sm">
                    Mot de passe
                  </Label>
                  <div className="relative">
                    <Lock className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-[#1F3A8A]/40" />
                    <Input
                      id="password"
                      type="password"
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="h-14 pl-12 bg-slate-50 border-slate-200 rounded-xl text-base focus:border-[#1F3A8A] focus:ring-[#1F3A8A]/20 focus:bg-white transition-colors"
                      data-testid="login-password-input"
                      disabled={loading}
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  className="w-full h-14 bg-[#1F3A8A] hover:bg-[#162d6e] text-white font-semibold text-base rounded-xl shadow-lg shadow-[#1F3A8A]/30 transition-all duration-200 active:scale-[0.98]"
                  disabled={loading}
                  data-testid="login-submit-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                      Connexion...
                    </>
                  ) : (
                    <>
                      Se connecter
                      <ArrowRight className="ml-2 h-5 w-5" />
                    </>
                  )}
                </Button>
              </form>
            </div>
            
            {/* Desktop: Original form */}
            <div className="hidden lg:block">
              {/* Form Header */}
              <div className="mb-10">
                <h2 className="text-3xl font-bold text-slate-900 tracking-tight">Connexion</h2>
                <p className="text-slate-500 mt-2">Entrez vos identifiants pour accéder à l'application</p>
              </div>

              {/* Login Form */}
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="email-desktop" className="text-slate-700 font-medium">Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                    <Input
                      id="email-desktop"
                      type="email"
                      placeholder="votre@email.fr"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      autoCapitalize="none"
                      autoCorrect="off"
                      spellCheck={false}
                      className="h-14 pl-12 bg-white border-slate-200 rounded-2xl text-base focus:border-[#1F3A8A] focus:ring-[#1F3A8A]/20"
                      data-testid="login-email-input-desktop"
                      disabled={loading}
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="password-desktop" className="text-slate-700 font-medium">Mot de passe</Label>
                  <div className="relative">
                    <Lock className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                    <Input
                      id="password-desktop"
                      type="password"
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="h-14 pl-12 bg-white border-slate-200 rounded-2xl text-base focus:border-[#1F3A8A] focus:ring-[#1F3A8A]/20"
                      data-testid="login-password-input-desktop"
                      disabled={loading}
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  className="w-full h-14 bg-[#1F3A8A] hover:bg-[#162d6e] text-white font-semibold text-base rounded-2xl shadow-lg shadow-[#1F3A8A]/30 transition-all duration-200 hover:shadow-xl hover:-translate-y-0.5"
                  disabled={loading}
                  data-testid="login-submit-btn-desktop"
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                      Connexion en cours...
                    </>
                  ) : (
                    <>
                      Se connecter
                      <ArrowRight className="ml-2 h-5 w-5" />
                    </>
                  )}
                </Button>
              </form>
            </div>

            {/* Help Text */}
            <p className="text-center text-slate-400 text-sm mt-6 lg:mt-8">
              Problème de connexion ? Contactez l'administrateur
            </p>
            
            {/* Mobile: Brand info */}
            <div className="lg:hidden mt-8 text-center">
              <p className="text-[#1F3A8A]/50 text-xs font-medium tracking-wide">
                UNE MARQUE DU GROUPE MCB
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
